import { useMemo } from "react";
import {
  Coordinate,
  PointPopup,
  RenderPointType,
  RenderRouteType,
  RoutePopup,
  RouteTransportTypes,
} from "@qubictron/maplib";
import { SearchReferenceModel } from "entities/shared/shipmentCard.types";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { panelStore } from "entities/Panel";
import { useOpenedCardSearchQuery } from "entities/RailSearchCard";
import { railSearchCardStore } from "entities/RailSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { MapPoints } from "entities/shared/mapPointsModel";
import { RouteReturnType } from "entities/shared/mapRouteModel";
import { subscriptionStore } from "entities/Subscription";
import { useAppSelector } from "shared/lib/redux";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";

type UseRailMapContentProps = {
  isMobile: boolean;
  showPanel: () => void;
  isMapLoaded: boolean;
  isDisabled: boolean;
};

export type RouteMarkerPoint = {
  route: Coordinate;
  transport: Nullable<ValueOf<typeof RouteTransportTypes>>;
};

type UseRailMapContentReturnType = {
  isRailRouteError: boolean;
  showRailRoute: (reference: SearchReferenceModel) => void;
  points: RenderPointType[];
  renderRoute: RenderRouteType;
  renderRoutePoints: RenderPointType[];
};

export const useRailMapContent = ({
  isMobile,
  showPanel,
  isMapLoaded,
  isDisabled,
}: UseRailMapContentProps): UseRailMapContentReturnType => {
  const currentView = useAppSelector(panelStore.selectors.currentView);
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const searchCards = useAppSelector(railSearchCardStore.selectors.searchCards);
  const { isCurrentViewMain, isCurrentViewOpenedCard } = usePanelViews();
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  const {
    searchResponse: cardData,
    isSearchSuccess,
    isSearchError,
  } = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const points = useMemo(() => {
    if (!isMapLoaded || isDisabled) return [];

    if (!isCurrentViewMain) {
      return [];
    }

    const points = new MapPoints({
      searchCards:
        searchCards?.map((card) => {
          const firstPoint = card.routes?.[0]?.from;
          const lastPoint = card.routes?.[card.routes.length - 1]?.to;
          return {
            currentPin:
              firstPoint?.lat != null && firstPoint?.lng != null ? { lat: firstPoint.lat, lng: firstPoint.lng } : null,
            fromDirection: firstPoint?.name ?? null,
            toDirection: lastPoint?.name ?? null,
            reference: card.reference,
          };
        }) ?? [],
      type: RouteTransportTypes.RAIL,
      savedCards: [],
    }).points;

    return points.map((point) => ({
      ...point,
      popup: (
        <RoutePopup
          from={point.fromDirection ?? undefined}
          to={point.toDirection ?? undefined}
          shipmentType={RouteTransportTypes.RAIL}
          reference={point.reference ?? ""}
          onClick={() => showRoute(point.reference ?? "")}
        />
      ),
    }));
  }, [currentView, searchCards.length, isMapLoaded, shipment]);

  const route = useMemo((): RouteReturnType => {
    const route = { points: [], route: { route: [] } };
    if (!isMapLoaded || isDisabled) return route;
    if (!isCurrentViewOpenedCard && !cardData) return route;
    return cardData?.route?.renderRoute ?? route;
  }, [currentView, cardData?.rawData, openedCard, isMapLoaded, points.length]);

  const showRoute = (reference: SearchReferenceModel) => {
    const targetCard = searchCards.find((card) => card.reference === reference);
    if (targetCard) {
      openCard(targetCard);
    }
    if (isMobile) {
      showPanel();
    }
  };

  const renderRoutePoints = useMemo(() => {
    return route.points.map((point) => ({
      ...point,
      popup: <PointPopup label={point.label ?? ""} shipmentType={RouteTransportTypes.RAIL} />,
    }));
  }, [route.points]);

  return {
    showRailRoute: showRoute,
    isRailRouteError:
      isCurrentViewOpenedCard &&
      (isSearchError || (isSearchSuccess && (!cardData?.isSuccess || !cardData?.routes?.length))),
    points,
    renderRoute: route.route,
    renderRoutePoints,
  };
};
